'use client';

import { useEffect, useRef } from 'react';
import { usePaginator } from '@commons/Paginator/PaginatorContext';
import './_blurb-list.carousel.scss';

export default function BlurbListCarousel({ children, className = '' }) {
  const { items, index, setIndex, setScrollDirection } = usePaginator();

  const containerRef = useRef(null);
  const scrollPortRef = useRef(null);
  const scrollLaneRef = useRef(null);
  const isProgrammaticScroll = useRef(false);
  const previousScrollLeft = useRef(0);
  const timeout = useRef(null);
  const syncedIndex = useRef(null);

  /* --------------------------------------------------
   * USER SCROLL → beräkna index
   * -------------------------------------------------- */
  const handleScroll = (e) => {
    clearTimeout(timeout.current);

    timeout.current = setTimeout(() => {
      const scrollPort = e.target;
      const item = scrollLaneRef.current.firstElementChild;

      if (!item) return;

      const itemWidth = item.getBoundingClientRect().width;
      let nextIndex = Math.round(scrollPort.scrollLeft / itemWidth);

      const maxScrollLeft = scrollPort.scrollWidth - scrollPort.clientWidth;
      let isAtEnd = maxScrollLeft > 0 && Math.abs(scrollPort.scrollLeft - maxScrollLeft) < 5;

      // Fallback: Check if the last actual object is visually entirely inside the right edge of the viewport
      if (!isAtEnd && items && items.length > 0) {
        const containerItems = scrollLaneRef.current.children;
        const lastItemNode = containerItems[items.length - 1];
        if (lastItemNode) {
          const portRect = scrollPort.getBoundingClientRect();
          const lastRect = lastItemNode.getBoundingClientRect();
          if (lastRect.right <= portRect.right + 5) {
            isAtEnd = true;
          }
        }
      }

      if (isAtEnd) {
        nextIndex = items.length - 1;
      }

      if (scrollPort.scrollLeft - previousScrollLeft.current > 0) {
        setScrollDirection('forward');
      } else {
        setScrollDirection('backward');
      }
      previousScrollLeft.current = scrollPort.scrollLeft;

      syncedIndex.current = nextIndex;
      setIndex(nextIndex);
    }, 100);
  };

  const isFirstRender = useRef(true);

  useEffect(() => {
    // Using the parent element as it is needed to wrap the carousel in order to use the visible overflow
    const scrollPortIsWrapped = containerRef.current.parentElement.classList.contains('SplxListBlock__carouselWrapper');
    scrollPortRef.current = scrollPortIsWrapped ? containerRef.current.parentElement : containerRef.current;
    scrollLaneRef.current = containerRef.current;

    scrollPortRef.current.addEventListener('scroll', handleScroll);

    return () => {
      scrollPortRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  /* --------------------------------------------------
   * CLICK / EXTERN index → scrollTo
   * -------------------------------------------------- */
  useEffect(() => {
    if (!scrollPortRef.current) return;
    const item = scrollLaneRef.current.firstElementChild;
    if (!item) return;

    if (syncedIndex.current === index) {
      isFirstRender.current = false;
      return;
    }

    const itemWidth = item.offsetWidth;
    let targetLeft = index * itemWidth;

    // Limit target bounds natively to layout bounds
    const maxScrollLeft = scrollPortRef.current.scrollWidth - scrollPortRef.current.clientWidth;

    if (targetLeft > maxScrollLeft) {
      // Index requests a target exceeding natural scroll bounds.
      if (index < syncedIndex.current) {
        targetLeft = Math.max(0, scrollPortRef.current.scrollLeft - itemWidth);
      } else {
        targetLeft = maxScrollLeft;
      }
    }

    syncedIndex.current = index;
    isProgrammaticScroll.current = true;

    if (isFirstRender.current) {
      // scrollTo({ behavior: 'auto' }) is unreliable in Safari on grid
      // scroll containers with scroll-snap-type; direct assignment always works.
      scrollPortRef.current.scrollLeft = targetLeft;
    } else {
      scrollPortRef.current.scrollTo({ left: targetLeft, behavior: 'smooth' });
    }

    isFirstRender.current = false;

    // Fallback flag release if no scroll events fire (e.g. already at exact position)
    const id = setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 500);

    return () => clearTimeout(id);
  }, [index]);

  if (!children) return null;

  return (
    <div ref={containerRef} className={`BlurbList__carousel ${className}`}>
      {children}
    </div>
  );
}
